/*
  🌿 Karma — lecture des vidéos maîtrisée
  Problème d'origine (mesuré le 2026-08-17 sur karma-cosmetiques.com) : 23 vidéos sur la
  page produit, 21 en autoplay, 22 hors écran qui se téléchargeaient quand même. Le
  navigateur n'ouvrant que ~6 connexions par domaine, elles monopolisaient tout pendant
  21 à 25 s — d'où les images qui n'arrivaient pas et le bouton « Ajouter au panier »
  sans réaction.

  Dans les deux modes, la vidéo est écrite avec preload="none" : elle ne télécharge rien
  tant qu'on n'en a pas besoin.

  MODE « clic » (data-mode="clic") — demandé par Gita le 2026-08-17 :
    « qu'il y ait la barre, que je puisse revenir en arrière, et un bouton Play : le client
      clique s'il veut voir la vidéo. »
    Rien ne démarre seul → rien ne se télécharge avant le clic (le plus rapide).
    Un bouton Play couvre la vidéo ; au clic : lecture AVEC le son + commandes natives
    (barre de progression, pause, retour en arrière). Pause si la vidéo quitte l'écran.

  MODE « auto » — la vidéo tourne en boucle, muette, façon GIF, pour les démonstrations
    courtes. Elle ne se charge et ne démarre qu'en arrivant à l'écran.
    Corrigé le 2026-08-17 : un navigateur refuse parfois de démarrer (plusieurs vidéos à la
    même milliseconde, données insuffisantes, retour d'onglet) — on retente jusqu'à 6 fois.
    Le son activé par la visiteuse n'est jamais recoupé.
*/

let compteurDemarrages = 0;

const ICONE_MUET = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M11 5 6.5 9H3v6h3.5L11 19V5Z"/><path stroke="currentColor" stroke-width="2" stroke-linecap="round" d="m16 9 5 6m0-6-5 6"/></svg>';
const ICONE_SON = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M11 5 6.5 9H3v6h3.5L11 19V5Z"/><path stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12"/></svg>';
const ICONE_PLAY = '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path fill="currentColor" d="M8 5.5v13a.5.5 0 0 0 .77.42l10-6.5a.5.5 0 0 0 0-.84l-10-6.5A.5.5 0 0 0 8 5.5Z"/></svg>';

class KarmaVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.video.removeAttribute('autoplay');
    if (!this.video.hasAttribute('data-karma-preload')) this.video.preload = 'none';
    this.video.playsInline = true;

    this.mode = this.dataset.mode === 'clic' ? 'clic' : 'auto';

    if (this.mode === 'clic') {
      this.initClic();
      return;
    }

    this.video.muted = true;
    this.video.addEventListener('volumechange', () => {
      this._sonVoulu = !this.video.muted;
      this.majBouton();
    });

    if (this.dataset.son === 'bouton') this.creerBoutonSon();

    const sansAnimation = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (sansAnimation || !('IntersectionObserver' in window)) {
      this.video.controls = true;
      return;
    }

    this.observer = new IntersectionObserver(
      (entrees) => entrees.forEach((e) => (e.isIntersecting ? this.lire() : this.pauser())),
      { rootMargin: '200px 0px', threshold: 0.01 }
    );
    this.observer.observe(this);

    this._surRetour = () => {
      if (!document.hidden && this._visible) this.tenter(0);
    };
    document.addEventListener('visibilitychange', this._surRetour);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this._minuteur);
    if (this._surRetour) document.removeEventListener('visibilitychange', this._surRetour);
  }

  /* ---------- mode « clic » : le visiteur décide ---------- */

  initClic() {
    const v = this.video;
    v.controls = true; // barre de progression, pause, retour en arrière

    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'karma-video__play';
    b.innerHTML = ICONE_PLAY;
    b.setAttribute('aria-label', 'Lire la vidéo');
    b.addEventListener('click', (e) => {
      e.preventDefault();
      // Le clic autorise le son : c'est la seule façon d'en avoir dès le départ.
      v.muted = false;
      v.load();
      const lancer = () => {
        const p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { v.muted = true; v.play().catch(() => {}); });
        }
      };
      if (v.readyState >= 2) lancer();
      else v.addEventListener('canplay', lancer, { once: true });
      this.classList.add('is-lancee');
    });
    this.appendChild(b);
    this.boutonPlay = b;

    // La vidéo qui quitte l'écran s'arrête : ni son ni téléchargement dans le vide.
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entrees) => entrees.forEach((e) => { if (!e.isIntersecting) this.pauser(); }),
        { rootMargin: '100px 0px', threshold: 0.01 }
      );
      this.observer.observe(this);
    }

    // Si le visiteur relance depuis les commandes natives, le bouton doit rester masqué.
    v.addEventListener('play', () => this.classList.add('is-lancee'));
  }

  /* ---------- mode « auto » : boucle muette, façon GIF ---------- */

  creerBoutonSon() {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'karma-video__son';
    b.innerHTML = ICONE_MUET;
    b.setAttribute('aria-label', 'Activer le son');
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.video.muted = !this.video.muted;
      this._sonVoulu = !this.video.muted;
      if (this.video.paused) this.tenter(0);
      this.majBouton();
    });
    this.appendChild(b);
    this.bouton = b;
  }

  majBouton() {
    if (!this.bouton) return;
    const actif = !this.video.muted;
    this.bouton.innerHTML = actif ? ICONE_SON : ICONE_MUET;
    this.bouton.setAttribute('aria-label', actif ? 'Couper le son' : 'Activer le son');
    this.bouton.classList.toggle('is-actif', actif);
  }

  lire() {
    const v = this.video;
    if (!v) return;
    this._visible = true;
    if (!this._sonVoulu) v.muted = true;

    if (v.readyState < 2 && !this._chargementLance) {
      this._chargementLance = true;
      v.addEventListener('canplay', () => this.tenter(0), { once: true });
      v.load();
    }

    const decalage = (compteurDemarrages++ % 6) * 120;
    clearTimeout(this._minuteur);
    this._minuteur = setTimeout(() => this.tenter(0), decalage);
  }

  tenter(essai) {
    const v = this.video;
    if (!v || !this._visible || essai > 6 || !v.paused) return;

    const promesse = v.play();
    if (promesse && typeof promesse.catch === 'function') {
      promesse.catch(() => {
        if (this._sonVoulu && !v.muted && essai >= 2) v.muted = true;
        clearTimeout(this._minuteur);
        this._minuteur = setTimeout(() => this.tenter(essai + 1), 300 + essai * 250);
      });
    }
  }

  pauser() {
    this._visible = false;
    clearTimeout(this._minuteur);
    if (this.video && !this.video.paused) this.video.pause();
  }
}

if (!customElements.get('karma-video')) {
  customElements.define('karma-video', KarmaVideo);
}
